import React, { useEffect } from 'react';
import { Shield, AlertTriangle, Brain, Activity, Headphones, Award, ShoppingBag, CheckCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  useEffect(() => {
    // Scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fadeIn');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animated').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.loading('Generating your safety guide...', { id: 'pdf-download' });
      
      const response = await axios.get(`${API}/download-guide`, {
        responseType: 'blob',
        timeout: 30000 // 30 second timeout
      });
      
      // Check if we actually got a PDF
      if (response.data.type !== 'application/pdf' && response.data.size < 1000) {
        throw new Error('Invalid PDF received');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Boxing_Safety_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Safety guide downloaded successfully!', { id: 'pdf-download' });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(`Download failed: ${error.message || 'Please try again'}`, { id: 'pdf-download' });
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation Header */}
      <header className="boxing-header">
        <div className="nav-wrapper">
          <div className="logo">
            <Shield className="logo-icon" />
            <span>Boxing Safety</span>
          </div>
          <nav className="boxing-nav">
            <button onClick={() => scrollToSection('why-matters')} className="nav-link">Why It Matters</button>
            <button onClick={() => scrollToSection('training-habits')} className="nav-link">Training Tips</button>
            <button onClick={() => scrollToSection('equipment')} className="nav-link">Equipment</button>
            <button onClick={() => scrollToSection('checklist')} className="nav-link">Checklist</button>
            <Button onClick={handleDownloadPDF} className="btn-download">
              <Download size={18} />
              Download Guide
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content animated">
            <h1 className="hero-title">How to Reduce Concussions and Head Trauma in Boxing</h1>
            <p className="hero-subtitle">
              Boxing is a high-impact sport that demands respect for both skill and safety. 
              Protecting your brain is essential for long-term health and a successful career in the ring.
            </p>
            <div className="hero-actions">
              <Button onClick={() => scrollToSection('why-matters')} className="btn-primary btn-large">
                Learn About Safety
              </Button>
              <Button onClick={handleDownloadPDF} className="btn-secondary btn-large">
                <Download size={20} />
                Get PDF Guide
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Concussions Matter */}
      <section id="why-matters" className="content-section bg-subtle">
        <div className="container">
          <div className="section-header animated">
            <Brain className="section-icon" />
            <h2 className="section-title">Why Concussions Matter in Boxing</h2>
          </div>
          <div className="content-grid">
            <Card className="info-card animated delay-200ms">
              <CardHeader>
                <CardTitle>What Is a Concussion?</CardTitle>
              </CardHeader>
              <CardContent>
                <p>A concussion is a brain injury caused by a blow to the head that makes the brain move rapidly inside the skull. It disrupts normal brain function and can cause symptoms like dizziness, confusion, and headaches.</p>
              </CardContent>
            </Card>
            <Card className="info-card animated delay-300ms">
              <CardHeader>
                <CardTitle>Short-Term Risks</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="risk-list">
                  <li>Headaches and dizziness</li>
                  <li>Confusion and disorientation</li>
                  <li>Nausea and balance problems</li>
                  <li>Sensitivity to light and sound</li>
                  <li>Difficulty concentrating</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="info-card animated delay-400ms">
              <CardHeader>
                <CardTitle>Long-Term Consequences</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="risk-list">
                  <li>Memory and cognitive issues</li>
                  <li>Chronic headaches</li>
                  <li>Mood changes and depression</li>
                  <li>Increased risk of future concussions</li>
                  <li>Potential CTE (Chronic Traumatic Encephalopathy)</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="alert-box animated delay-500ms">
            <AlertTriangle className="alert-icon" />
            <p className="alert-text">
              <strong>Important:</strong> Concussions can happen even without knockouts. Repeated lighter blows to the head accumulate and cause damage over time.
            </p>
          </div>
        </div>
      </section>

      {/* How Concussions Happen */}
      <section className="content-section">
        <div className="container">
          <div className="section-header animated">
            <Activity className="section-icon" />
            <h2 className="section-title">How Concussions Happen in Boxing</h2>
          </div>
          <div className="causes-grid">
            <div className="cause-item animated delay-200ms">
              <div className="cause-number">1</div>
              <h3>Repeated Head Impacts</h3>
              <p>Constant punches to the head, even during light sparring, cause cumulative brain trauma over weeks and months.</p>
            </div>
            <div className="cause-item animated delay-300ms">
              <div className="cause-number">2</div>
              <h3>Poor Defense</h3>
              <p>Neglecting defensive fundamentals like guard position, head movement, and footwork leads to taking more direct hits.</p>
            </div>
            <div className="cause-item animated delay-400ms">
              <div className="cause-number">3</div>
              <h3>Excessive Hard Sparring</h3>
              <p>Training at full intensity too often increases head trauma risk. Smart training means balancing intensity with safety.</p>
            </div>
            <div className="cause-item animated delay-500ms">
              <div className="cause-number">4</div>
              <h3>Lack of Rest After Injuries</h3>
              <p>Returning to training too quickly after head impacts prevents proper brain recovery and increases injury severity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Habits */}
      <section id="training-habits" className="content-section bg-subtle">
        <div className="container">
          <div className="section-header animated">
            <Award className="section-icon" />
            <h2 className="section-title">Training Habits That Reduce Head Trauma</h2>
          </div>
          <div className="habits-grid">
            <Card className="habit-card animated delay-200ms">
              <CardContent>
                <div className="habit-icon-wrapper">
                  <CheckCircle className="habit-icon" />
                </div>
                <h3>Limit Hard Sparring</h3>
                <p>Schedule hard sparring sessions strategically—not every session needs to be at full power. Focus on technical sparring 70-80% of the time.</p>
              </CardContent>
            </Card>
            <Card className="habit-card animated delay-300ms">
              <CardContent>
                <div className="habit-icon-wrapper">
                  <CheckCircle className="habit-icon" />
                </div>
                <h3>Master Defensive Skills</h3>
                <p>Invest time in guard work, footwork drills, head movement, and slipping. Good defense means fewer hits to your head.</p>
              </CardContent>
            </Card>
            <Card className="habit-card animated delay-400ms">
              <CardContent>
                <div className="habit-icon-wrapper">
                  <CheckCircle className="habit-icon" />
                </div>
                <h3>Take Adequate Rest</h3>
                <p>If you take a hard shot or feel symptoms, stop training immediately. Allow at least 7-14 days recovery before returning to contact.</p>
              </CardContent>
            </Card>
            <Card className="habit-card animated delay-500ms">
              <CardContent>
                <div className="habit-icon-wrapper">
                  <CheckCircle className="habit-icon" />
                </div>
                <h3>Be Honest About Symptoms</h3>
                <p>Never hide concussion symptoms from coaches or training partners. Early recognition prevents long-term damage.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Headguards Section */}
      <section id="equipment" className="content-section">
        <div className="container">
          <div className="section-header animated">
            <Headphones className="section-icon" />
            <h2 className="section-title">Why Headguards Matter (and Their Limits)</h2>
          </div>
          <div className="headguard-content animated delay-200ms">
            <div className="headguard-benefits">
              <h3>What Headguards Do:</h3>
              <ul className="benefit-list">
                <li>Reduce cuts, bruises, and facial injuries</li>
                <li>Absorb some impact force</li>
                <li>Protect ears and cheekbones</li>
                <li>Required in amateur boxing for safety</li>
              </ul>
            </div>
            <div className="headguard-limits">
              <h3>Important Limitation:</h3>
              <div className="warning-box">
                <AlertTriangle className="warning-icon" />
                <p><strong>Headguards do NOT fully prevent concussions.</strong> They reduce surface damage but cannot stop the brain from moving inside the skull during impact. Smart training habits remain essential.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gloves Section */}
      <section className="content-section bg-subtle">
        <div className="container">
          <div className="section-header animated">
            <Shield className="section-icon" />
            <h2 className="section-title">Why 16 oz Gloves Are Important for Sparring</h2>
          </div>
          <div className="gloves-grid">
            <Card className="glove-card animated delay-200ms">
              <CardContent>
                <h3>More Padding = Less Impact</h3>
                <p>16 oz gloves have significantly more padding than competition gloves (8-10 oz), which distributes the force of punches over a larger area and reduces head trauma.</p>
              </CardContent>
            </Card>
            <Card className="glove-card animated delay-300ms">
              <CardContent>
                <h3>Encourages Technical Sparring</h3>
                <p>Heavier gloves slow down punches slightly, promoting controlled, technical work instead of full-power shots during training sessions.</p>
              </CardContent>
            </Card>
            <Card className="glove-card animated delay-400ms">
              <CardContent>
                <h3>Industry Standard</h3>
                <p>16 oz gloves are the worldwide standard for sparring in professional gyms. Always use them during training—save lighter gloves for competition only.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Recommended Headguards */}
      <section className="content-section">
        <div className="container">
          <div className="section-header animated">
            <ShoppingBag className="section-icon" />
            <h2 className="section-title">Recommended Headguards for Sparring</h2>
          </div>
          <div className="products-grid">
            <Card className="product-card animated delay-200ms">
              <CardHeader>
                <CardTitle>Winning Headgear</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="product-description">Premium Japanese-made headgear with excellent padding and protection. Favored by professional boxers worldwide.</p>
                <ul className="product-features">
                  <li>Superior impact absorption</li>
                  <li>Comfortable fit</li>
                  <li>Excellent visibility</li>
                </ul>
                <a href="https://www.winning-usa.com" target="_blank" rel="noopener noreferrer" className="product-link">
                  Visit Winning USA →
                </a>
              </CardContent>
            </Card>

            <Card className="product-card animated delay-300ms">
              <CardHeader>
                <CardTitle>Cleto Reyes Headgear</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="product-description">High-quality Mexican craftsmanship with great cheek and chin protection. Trusted by champions for decades.</p>
                <ul className="product-features">
                  <li>Full face protection</li>
                  <li>Durable leather construction</li>
                  <li>Adjustable fit system</li>
                </ul>
                <a href="https://www.cletoreyes.com" target="_blank" rel="noopener noreferrer" className="product-link">
                  Visit Cleto Reyes →
                </a>
              </CardContent>
            </Card>

            <Card className="product-card animated delay-400ms">
              <CardHeader>
                <CardTitle>Ringside Headgear</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="product-description">Affordable and reliable option for beginners and training. Good protection at a reasonable price point.</p>
                <ul className="product-features">
                  <li>Budget-friendly</li>
                  <li>Good padding quality</li>
                  <li>Available in multiple sizes</li>
                </ul>
                <a href="https://www.ringside.com" target="_blank" rel="noopener noreferrer" className="product-link">
                  Visit Ringside →
                </a>
              </CardContent>
            </Card>

            <Card className="product-card animated delay-500ms">
              <CardHeader>
                <CardTitle>Adidas Amateur Headgear</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="product-description">Official amateur boxing headgear meeting AIBA standards. Perfect for competitive amateur boxers.</p>
                <ul className="product-features">
                  <li>Competition approved</li>
                  <li>Lightweight design</li>
                  <li>Full head coverage</li>
                </ul>
                <a href="https://www.adidas.com/us/boxing" target="_blank" rel="noopener noreferrer" className="product-link">
                  Visit Adidas →
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="buying-tips animated delay-600ms">
            <h3>What to Look For When Buying:</h3>
            <div className="tips-grid">
              <div className="tip-item">
                <CheckCircle className="tip-icon" />
                <span>Proper fit—snug but not too tight</span>
              </div>
              <div className="tip-item">
                <CheckCircle className="tip-icon" />
                <span>Good cheek and temple protection</span>
              </div>
              <div className="tip-item">
                <CheckCircle className="tip-icon" />
                <span>Clear visibility from all angles</span>
              </div>
              <div className="tip-item">
                <CheckCircle className="tip-icon" />
                <span>Quality padding that doesn't compress easily</span>
              </div>
              <div className="tip-item">
                <CheckCircle className="tip-icon" />
                <span>Durable construction and materials</span>
              </div>
              <div className="tip-item">
                <CheckCircle className="tip-icon" />
                <span>Secure straps or lacing system</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Buy */}
      <section className="content-section bg-subtle">
        <div className="container">
          <div className="section-header animated">
            <ShoppingBag className="section-icon" />
            <h2 className="section-title">Where to Buy Safe Boxing Gear</h2>
          </div>
          <div className="stores-grid">
            <Card className="store-card animated delay-200ms">
              <CardContent>
                <h3>Official Brand Websites</h3>
                <p>Purchase directly from manufacturers for authentic gear and warranty support.</p>
              </CardContent>
            </Card>
            <Card className="store-card animated delay-300ms">
              <CardContent>
                <h3>Boxing Specialty Stores</h3>
                <p>Visit local boxing shops where staff can help with proper fitting and expert advice.</p>
              </CardContent>
            </Card>
            <Card className="store-card animated delay-400ms">
              <CardContent>
                <h3>Certified Sports Retailers</h3>
                <p>Trusted retailers like Title Boxing, Everlast.com, and FighterXFashion.com offer quality equipment.</p>
              </CardContent>
            </Card>
          </div>
          <div className="warning-box animated delay-500ms">
            <AlertTriangle className="warning-icon" />
            <p><strong>Avoid cheap, low-quality gear.</strong> Your brain health is not worth saving a few dollars. Invest in proper equipment from reputable brands.</p>
          </div>
        </div>
      </section>

      {/* Safety Checklist */}
      <section id="checklist" className="content-section">
        <div className="container">
          <div className="section-header animated">
            <CheckCircle className="section-icon" />
            <h2 className="section-title">Safety Checklist for Sparring</h2>
          </div>
          <div className="checklist-box animated delay-200ms">
            <div className="checklist-item">
              <div className="checkbox-icon">
                <CheckCircle />
              </div>
              <div className="checklist-content">
                <h3>Headguard Fitted Properly</h3>
                <p>Ensure your headgear is snug, doesn't move during training, and provides full coverage.</p>
              </div>
            </div>
            <div className="checklist-item">
              <div className="checkbox-icon">
                <CheckCircle />
              </div>
              <div className="checklist-content">
                <h3>16 oz Gloves in Good Condition</h3>
                <p>Check that padding hasn't compressed and gloves are clean and properly maintained.</p>
              </div>
            </div>
            <div className="checklist-item">
              <div className="checkbox-icon">
                <CheckCircle />
              </div>
              <div className="checklist-content">
                <h3>Mouthguard Worn</h3>
                <p>Always wear a properly fitted mouthguard—it protects teeth and reduces concussion risk.</p>
              </div>
            </div>
            <div className="checklist-item">
              <div className="checkbox-icon">
                <CheckCircle />
              </div>
              <div className="checklist-content">
                <h3>Coach Supervision</h3>
                <p>Never spar without an experienced coach present to monitor intensity and safety.</p>
              </div>
            </div>
            <div className="checklist-item">
              <div className="checkbox-icon">
                <CheckCircle />
              </div>
              <div className="checklist-content">
                <h3>Stop If Symptoms Appear</h3>
                <p>Immediately stop training if you experience dizziness, confusion, headaches, or nausea.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="final-section">
        <div className="container">
          <div className="final-content animated">
            <h2 className="final-title">Your Health Is Your Career</h2>
            <p className="final-text">
              Smart training doesn't make you weaker—it keeps you in the sport longer. Protecting your brain 
              means you can train harder, fight better, and enjoy boxing for decades to come. Champions are built 
              on discipline, technique, and respect for long-term health.
            </p>
            <p className="final-text">
              Remember: Every punch matters. Every decision matters. Train smart, stay safe, and protect what matters most.
            </p>
            <div className="final-actions">
              <Button onClick={handleDownloadPDF} className="btn-primary btn-large">
                <Download size={20} />
                Download Complete Safety Guide
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <Shield className="logo-icon" />
                <span>Boxing Safety</span>
              </div>
              <p className="footer-description">
                Educational resource dedicated to reducing head trauma and promoting safe training practices in boxing.
              </p>
            </div>
            <div className="footer-section">
              <h4>Important Links</h4>
              <ul className="footer-links">
                <li><a href="https://www.usaboxing.org" target="_blank" rel="noopener noreferrer">USA Boxing</a></li>
                <li><a href="https://www.wbc.com" target="_blank" rel="noopener noreferrer">World Boxing Council</a></li>
                <li><a href="https://www.brain-injury.com" target="_blank" rel="noopener noreferrer">Brain Injury Info</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Disclaimer</h4>
              <p className="footer-disclaimer">
                This site provides educational information only. Always consult with medical professionals and certified coaches for personalized advice.
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Boxing Safety Education. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
